import type { BaseQueryFn, QueryActionCreatorResult, QueryDefinition } from '@reduxjs/toolkit/query';
import type { FleetFE } from 'domain/systemAdmin';
import { SystemAdminFormTypes } from 'helper/types';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useListSystemTeamsQuery } from 'services/systemAdmin/teams';
import { getSelectedFleet } from 'store/slices/settingsPageSlice';
import {
    addSelectedTeamsToFleetForm,
    getSelectedTeamsFromFleetForm,
    getSystemFleetsFormType,
} from 'store/slices/systemFleet';

import SystemFleetCreateForm from './SystemFleetsCreateForm';
import SystemFleetEditForm from './SystemFleetsEditForm';

interface Props {
    refetch(): QueryActionCreatorResult<
        QueryDefinition<string, BaseQueryFn<any, unknown, unknown, {}, {}>, never, FleetFE[], 'systemFleets'>
    >;
    isFetchingFleets?: boolean;
}

const SystemFleetsForm = ({ refetch, isFetchingFleets }: Props) => {
    const dispatch = useDispatch();
    const systemFleetForm = useSelector(getSystemFleetsFormType);
    const selectedFleet = useSelector(getSelectedFleet);
    const fleetTeams = selectedFleet?.teams.map(_ => _.id) ?? [];
    const selectedTeamsFromFleetForm = useSelector(getSelectedTeamsFromFleetForm);

    const { systemTeams, isLoading: isLoadingSystemTeams } = useListSystemTeamsQuery('ListSystemTeams', {
        refetchOnMountOrArgChange: true,
        selectFromResult: state => ({
            ...state,
            systemTeams: state.data?.map(_ => ({ id: _.id, label: _.name })),
        }),
    });

    useEffect(() => {
        if (systemTeams && !selectedTeamsFromFleetForm && systemFleetForm === SystemAdminFormTypes.EDIT_FORM) {
            dispatch(addSelectedTeamsToFleetForm(fleetTeams));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    switch (systemFleetForm) {
        case SystemAdminFormTypes.EDIT_FORM:
            return (
                <SystemFleetEditForm
                    systemTeams={systemTeams}
                    isLoadingSystemTeams={isLoadingSystemTeams}
                    refetch={refetch}
                    isFetchingFleets={isFetchingFleets}
                />
            );
        case SystemAdminFormTypes.CREATE_FORM:
            return (
                <SystemFleetCreateForm
                    systemTeams={systemTeams}
                    isLoadingSystemTeams={isLoadingSystemTeams}
                    refetch={refetch}
                    isFetchingFleets={isFetchingFleets}
                />
            );
        default:
            return;
    }
};

export default SystemFleetsForm;
